import { useEffect, useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import type { RaceHitDto, RaceSearchResponse, WriteInRequest } from '@signalvote/shared';
import { assertNever, PartyPrimary, RaceStageKind } from '@signalvote/shared';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useRaceSearch } from '@/hooks/queries';
import { US_STATE_OPTIONS } from '@/lib/usStates';
import { cn } from '@/lib/utils';
import { Blank, type BlankFocus } from './Blank';
import { stageName } from './goalWords';
import { stateName } from './raceStages';
import { WriteInForm } from './WriteInForm';

const SEARCH_DELAY_MS = 300;
const STATES = US_STATE_OPTIONS.filter((s) => s.value);

/**
 * The sentence's opening, "In [state], I’m running for [race] in the [primary]", as the page that owns the race sees it. The
 * goal follows it once the race is set and any primary it asks is answered.
 */
export interface RaceOpening {
  /** Two letters; null until chosen. */
  state: string | null;
  /** Null until a race in that state is set. A race belongs to one state, so a new state empties it. */
  race: {
    name: string;
    /** Only a race with both party primaries asks which one; the answer is null until given, never guessed. */
    primary: { answer: PartyPrimary | null } | null;
  } | null;
  /** How the blanks get filled. Null for someone who may only read them. */
  edit: OpeningEdit | null;
}

export interface OpeningEdit {
  onState: (state: string) => void;
  race: RaceSearch;
  onPrimary: (primary: PartyPrimary) => void;
}

/** What the race blank offers: the catalog search, a write-in, and on the pages before sign-up, not knowing yet. */
export interface RaceSearch {
  /** The catalog race set now, marked in the results. */
  currentRaceId: string | null;
  /** The written-in race set now: the blank opens on its form. */
  writeIn: WriteInRequest | null;
  onPick: (race: RaceHitDto) => void;
  onWriteIn: (writeIn: WriteInRequest) => void;
  onNoDistrict?: () => void;
}

export const openingComplete = (opening: RaceOpening): boolean =>
  opening.race !== null && (opening.race.primary === null || opening.race.primary.answer !== null);

/**
 * The opening's words and blanks, as the first words of the first stage's sentence (`stage` names a primary the race doesn't
 * ask about) or, with no stage, alone.
 */
export function openingWords(opening: RaceOpening, stage: RaceStageKind | null, focus: BlankFocus): ReactNode[] {
  const { state, race, edit } = opening;
  const slot = (key: string, filled: string | null, empty: string, aria: { empty: string; filled: string }, panel: (edit: OpeningEdit, close: () => void) => ReactNode, wide?: boolean) =>
    edit ? (
      <Blank key={key} id={key} testId={`blank-${key}`} label={filled ?? empty} empty={!filled} aria={filled ? aria.filled : aria.empty} focus={focus} wide={wide}>
        {(close) => panel(edit, close)}
      </Blank>
    ) : (
      (filled ?? <span key={key} className="text-muted-foreground">{empty}</span>)
    );

  const words: ReactNode[] = [
    'In ',
    slot('state', state && stateName(state), 'choose a state', { empty: 'Choose a state', filled: 'Change state' }, (edit, close) => (
      <StateList
        current={state}
        onPick={(picked) => {
          edit.onState(picked);
          // A new state empties the race, so its blank opens next.
          if (picked === state) close();
          else focus.setOpen('race');
        }}
      />
    )),
    ', I’m running for ',
    slot('race', race?.name ?? null, 'find your race', { empty: 'Find your race', filled: 'Change race' }, (edit, close) => <RacePanel state={state} search={edit.race} onState={edit.onState} close={close} />, true),
  ];
  if (race?.primary) {
    const { answer } = race.primary;
    words.push(
      ' in the ',
      slot('primary', answer && `${answer} primary`, 'which primary?', { empty: 'Which primary?', filled: 'Change primary' }, (edit, close) => (
        <PrimaryPanel
          answer={answer}
          onAnswer={(party) => {
            if (party !== answer) edit.onPrimary(party);
            close();
          }}
        />
      )),
    );
  } else if (stage && stage !== RaceStageKind.General) words.push(` in the ${stageName(stage)}`);
  return words;
}

function StateList({ current, onPick }: { current: string | null; onPick: (state: string) => void }) {
  return (
    <Command className="rounded-md border">
      <CommandInput placeholder="Type a state" autoFocus />
      <CommandList className="max-h-[min(45vh,18rem)]">
        <CommandEmpty>No state matches that.</CommandEmpty>
        {STATES.map((s) => (
          <CommandItem key={s.value} value={s.label} onSelect={() => onPick(s.value)} className="justify-between">
            {s.label}
            {s.value === current && <Check aria-hidden className="size-4 text-primary" />}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}

/** The race blank: the state first when there is none, then one box for the race's name, place or office. */
function RacePanel({ state, search, onState, close }: { state: string | null; search: RaceSearch; onState: (state: string) => void; close: () => void }) {
  const [writing, setWriting] = useState(!!search.writeIn);
  const [typed, setTyped] = useState('');
  const [phrase, setPhrase] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setPhrase(typed), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typed]);
  const result = useRaceSearch(state ?? '', phrase);
  const pick = (race: RaceHitDto) => {
    search.onPick(race);
    close();
  };
  const link = 'text-sm font-medium text-primary hover:underline';
  const noDistrict = search.onNoDistrict && (
    <button
      type="button"
      className={link}
      onClick={() => {
        search.onNoDistrict?.();
        close();
      }}
    >
      I don’t know my district yet
    </button>
  );

  if (!state)
    return (
      <div className="space-y-3">
        <h3 className="text-base font-semibold">Which state is your race in?</h3>
        <StateList current={null} onPick={onState} />
        {noDistrict}
      </div>
    );
  if (writing)
    return (
      <div className="space-y-3">
        <WriteInForm
          state={state}
          initial={search.writeIn}
          onSubmit={(writeIn) => {
            search.onWriteIn(writeIn);
            close();
          }}
        />
        <button type="button" className={link} onClick={() => setWriting(false)}>
          Search the list instead
        </button>
      </div>
    );
  return (
    <div className="space-y-3">
      <input
        aria-label="Your race"
        autoFocus
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        placeholder={`Search ${stateName(state)}, like “House District 1”`}
        value={typed}
        autoComplete="off"
        onChange={(e) => setTyped(e.target.value)}
      />
      {(result.isError || result.isFetching || result.data) && (
        <div className="max-h-[min(45vh,22rem)] space-y-2 overflow-y-auto">
          {result.isError && (
            <p role="alert" className="text-sm text-destructive">
              {result.error.message}
            </p>
          )}
          {result.isFetching && !result.data && <p className="text-sm text-muted-foreground">Searching…</p>}
          {result.data && <SearchAnswer answer={result.data} currentRaceId={search.currentRaceId} onPick={pick} />}
        </div>
      )}
      <div className="flex flex-col items-start gap-1">
        <button type="button" className={link} onClick={() => setWriting(true)}>
          My race isn’t listed
        </button>
        {noDistrict}
      </div>
    </div>
  );
}

function SearchAnswer({ answer, currentRaceId, onPick }: { answer: RaceSearchResponse; currentRaceId: string | null; onPick: (race: RaceHitDto) => void }) {
  const list = (races: RaceHitDto[]) => races.map((r) => <RaceOption key={r.id} race={r} current={r.id === currentRaceId} onPick={onPick} />);
  const others = (races: RaceHitDto[]) => races.length > 0 && <p className="pt-2 text-sm text-muted-foreground">Or one of these:</p>;
  switch (answer.outcome) {
    case 'Match':
      return (
        <>
          <p className="text-sm font-medium">Is this your race?</p>
          <RaceOption race={answer.pick} current={answer.pick.id === currentRaceId} onPick={onPick} strong />
          {others(answer.races)}
          {list(answer.races)}
        </>
      );
    case 'DidYouMean':
      return (
        <>
          <p className="text-sm font-medium">Did you mean {answer.pick.name}?</p>
          <RaceOption race={answer.pick} current={answer.pick.id === currentRaceId} onPick={onPick} strong />
          {others(answer.races)}
          {list(answer.races)}
        </>
      );
    case 'NoMatch':
      return (
        <>
          <p className="text-sm font-medium">{answer.races.length ? 'We couldn’t find that exact race. Is it one of these?' : 'No races match that. Try the office or the place.'}</p>
          {list(answer.races)}
        </>
      );
    case 'Unpicked':
      return answer.races.length ? (
        <>
          <p className="text-sm font-medium">Pick your race:</p>
          {list(answer.races)}
        </>
      ) : (
        <p className="text-sm font-medium">No races match that. Try the office or the place.</p>
      );
    default:
      return assertNever(answer);
  }
}

function RaceOption({ race, current, strong, onPick }: { race: RaceHitDto; current: boolean; strong?: boolean; onPick: (race: RaceHitDto) => void }) {
  return (
    <button
      type="button"
      onClick={() => onPick(race)}
      className={cn('flex w-full items-baseline justify-between gap-3 rounded-lg border px-3 py-2 text-left hover:border-primary hover:bg-primary/5', strong && 'border-primary/50 bg-primary/5')}
    >
      <span>
        <span className="block font-semibold">{race.name}</span>
        <span className="block text-sm text-muted-foreground">
          {race.office} · {race.place}
        </span>
      </span>
      {current && <span className="text-xs font-semibold uppercase text-muted-foreground">Your race now</span>}
    </button>
  );
}

function PrimaryPanel({ answer, onAnswer }: { answer: PartyPrimary | null; onAnswer: (party: PartyPrimary) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">Which primary are you running in?</h3>
        <p className="text-sm text-muted-foreground">You run in your own party’s primary, then the general.</p>
      </div>
      <div className="space-y-1">
        {[PartyPrimary.Republican, PartyPrimary.Democratic].map((party) => (
          <button
            key={party}
            type="button"
            aria-pressed={answer === party}
            onClick={() => onAnswer(party)}
            className={cn('flex w-full rounded-md border px-3 py-2 text-left text-sm font-medium hover:bg-muted', answer === party && 'border-primary bg-primary/5')}
          >
            {party} primary
          </button>
        ))}
      </div>
    </div>
  );
}
